/**
 * The winner of a route test and where it took the day (19/10/2026), and
 * 21/10/2026: each container with the gateway it really uses — the route
 * test's next one where the gateway verdict blocked the winner (it took
 * over), or none where every ranked gateway is blocked (its drafts take
 * turns across the pool).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Trophy } from 'lucide-react';
import type { DropGenRouteTestWinnerRow } from '@trafficpulse/shared';

import { ContainerChip } from '../../shared/ContainerChip';

type Applied = DropGenRouteTestWinnerRow['appliedTo'][number];

const nameOf = (routeName: string | null, routeId: number) => routeName ?? `Gateway ${routeId}`;
const onWinner = (c: Applied) => c.uses === undefined || (c.uses !== null && !c.uses.takenOver);

const Moved: FC<{ c: Applied }> = ({ c }) => (
  <div className="flex flex-wrap items-center gap-1.5" data-testid="route-test-takeover">
    <ContainerChip name={c.name} color={c.color} className="!px-2 !text-[10.5px]" />
    {c.uses ? (
      <span>
        uses <b>{nameOf(c.uses.routeName, c.uses.routeId)}</b> <span className="font-mono">· {c.uses.sender}</span>
        <span className="text-muted-foreground"> — took over, the winner is blocked here</span>
      </span>
    ) : (
      <span className="text-muted-foreground">every ranked gateway is blocked here — its drafts take turns across the pool</span>
    )}
  </div>
);

export const WinnerBanner: FC<{ winner: DropGenRouteTestWinnerRow }> = ({ winner }) => {
  const moved = winner.appliedTo.filter((c) => !onWinner(c));
  return (
    <div className="mx-5 mb-2 flex flex-col gap-1.5 rounded-lg border border-success/40 bg-success/10 px-3 py-2 text-xs" data-testid="route-test-winner">
      <div className="flex flex-wrap items-center gap-2">
        <Trophy className="h-4 w-4 text-success" aria-hidden />
        <span className="font-semibold">{nameOf(winner.routeName, winner.routeId)}</span>
        <span className="font-mono">· {winner.sender}</span>
        <span className="text-muted-foreground">won today — the rest of the day goes through it in</span>
        {winner.appliedTo.filter(onWinner).map((c) => (
          <ContainerChip key={c.containerId} name={c.name} color={c.color} className="!px-2 !text-[10.5px]" />
        ))}
      </div>
      {moved.map((c) => (
        <Moved key={c.containerId} c={c} />
      ))}
    </div>
  );
};
